/**
 * **Le HUD progressif** — ⟦A-129 · C11⟧, direction 3.
 *
 * Mot de Mathieu, playtest : *« tu te retrouves avec beaucoup de chiffres et beaucoup de
 * calculs »*. Vécu à l'écran (`Studio.tsx`) : à la seconde zéro, 5 jauges chiffrées + le
 * multiplicateur + le palier + le niveau + l'XP + les Clicks — sept éléments qu'un joueur qui
 * vient de créer sa chaîne n'a encore aucun moyen de comprendre, puisqu'aucun n'a encore bougé.
 *
 * **La règle, telle que validée par Mathieu (« le 1 2 3, c'est exactement ça ») :**
 * à la création d'une partie, seuls les abonnés, l'énergie et la santé mentale sont visibles.
 * Chaque autre élément (réputation, fidélité, momentum, hype, argent, niveau/XP) apparaît **la
 * première fois qu'il bouge** — mêmes seuils que les leçons de `game/decouverte.ts` (« reputation
 * !== 50 », etc., pour celles qui ont un équivalent) — et son apparition **persiste pour la
 * run**, même si la valeur revient ensuite à son état neutre.
 *
 * ⚠️ **Le critère « a bougé » se juge sur l'ÉTAT, pas sur un historique qu'on n'a pas** (mot du
 * chantier C11, repris de la règle générale ⟦A-10⟧) : `visible()` recalcule donc TOUJOURS le
 * prédicat sur l'état courant, et ne consulte la mémoire persistée que pour ne jamais faire
 * REDISPARAÎTRE un élément déjà montré. Une partie EXISTANTE, chargée avec des jauges déjà non
 * neutres (réputation à 62 dès le premier rendu, par exemple), les montre donc immédiatement —
 * sans qu'aucun historique de leçon n'ait jamais existé pour elle.
 *
 * La mémoire de persistance est la MÊME que celle du mode découverte (`decouverteStore.vues`,
 * `localStorage` clefé par `runId`) : les ids ci-dessous coïncident déjà, pour la plupart, avec
 * des ids de leçon (`reputation`, `fidelite`, `momentum`, `fil`, `argent`) — quand l'info-bulle
 * correspondante se ferme, le HUD et la découverte retiennent donc EXACTEMENT le même fait, une
 * seule fois, au même endroit.
 */

import { SYSTEMES_TOUS_ACTIFS, type GameState, type Systemes } from "@maxime/shared";

import { contenu } from "./content";
import { ouvert } from "./escalier";

/**
 * Les éléments du HUD soumis à révélation progressive. `niveau` n'a pas de leçon équivalente
 * (aucune leçon de `decouverte.ts` ne porte sur le niveau/XP) : son id est propre au HUD, sans
 * collision possible avec un id de leçon.
 */
export type ElementHud = "reputation" | "fidelite" | "momentum" | "hype" | "argent" | "niveau";

export const ELEMENTS_HUD: readonly ElementHud[] = [
  "reputation",
  "fidelite",
  "momentum",
  "hype",
  "argent",
  "niveau",
];

/**
 * Un prédicat par élément — repris tel quel de `game/decouverte.ts` pour les quatre qui ont une
 * leçon d'état (`reputation`, `fidelite`, `momentum`, `fil`→`hype`), plus `argent` (même seuil
 * que la leçon `argent`, `ouvert(s, "argent")`) et `niveau` (propre au HUD : le niveau a monté,
 * ou de l'XP a été gagnée).
 */
const CONDITIONS: Record<ElementHud, (s: GameState) => boolean> = {
  reputation: (s) => s.reputation !== 50,
  fidelite: (s) => s.loyalty !== 50,
  momentum: (s) => Math.abs(s.momentum - 1) > 0.001,
  hype: (s) => s.flags.fil_decouvert === true,
  argent: (s) => ouvert(s, "argent"),
  niveau: (s) => s.level > 1 || s.xp > 0,
};

/**
 * ⟦C60 bis · A-177⟧ **Cet élément existe-t-il encore à l'écran ?** Matt (03/10/2026, arbitrage n° 2) :
 * *« La réputation : la retirer de l'écran. »* La jauge n'a plus d'effet que le jeu montre : le moteur la
 * fait bouger en silence, l'écran n'en parle plus. Éteint, `reputation` n'est ni révélée au premier
 * mouvement, ni montrée par « Tableau de bord complet » : rien ne la rallume que l'interrupteur. `true`
 * (ou un contenu qui ne dit rien) : le HUD d'avant, au caractère près.
 */
export function hudOuvert(id: ElementHud, sys: Pick<Systemes, "reputation"> = SYSTEMES_TOUS_ACTIFS): boolean {
  return id !== "reputation" || sys.reputation;
}

/**
 * `id` doit-il être visible dans le HUD, sachant l'état courant et la mémoire des éléments déjà
 * révélés dans cette run ? Prédicat courant OU mémoire — jamais l'un sans l'autre : c'est ce qui
 * fait qu'un élément apparu reste apparu, même si sa valeur revient ensuite au neutre.
 *
 * `sys` (⟦C60 bis⟧) : les interrupteurs du ménage ; absent, le jeu d'avant (tout visible).
 */
export function hudVisible(
  id: ElementHud,
  state: GameState,
  dejaRevele: ReadonlySet<string>,
  sys: Pick<Systemes, "reputation"> = SYSTEMES_TOUS_ACTIFS,
): boolean {
  return hudOuvert(id, sys) && (dejaRevele.has(id) || CONDITIONS[id](state));
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦C62 · lot tableau · A-179⟧ Le tableau de bord limpide : un « Détails » replié
// ─────────────────────────────────────────────────────────────────────────────
//
// Matt : *« Il faut que le jeu soit simple et limpide. »* L'audit du 21/10/2026 compte 5 lignes de chiffres au
// départ, 9 à la 2ᵉ nuit, 14 au million, et des jauges qui ne se nomment qu'au survol (jamais au toucher). Le
// critère : à tout moment le joueur voit ce qui peut le TUER ou le FAIRE GAGNER, avec son nom écrit ; le reste
// existe, se lit, mais se range derrière UN bouton « Détails », fermé par défaut.
//
// Ce module ne rend rien : il dit QUOI va où (pur, testé), `Studio.tsx` dessine. Le HUD progressif ci-dessus
// (⟦A-129 · C11⟧) reste le seul juge de « apparu ou pas » ; ce qui change ici, c'est « sous les yeux ou rangé ».

/**
 * Ce que le tableau limpide range dans « Détails », dans l'ordre où le joueur le lit. Les abonnés, l'énergie,
 * la santé mentale et les Clicks n'y sont JAMAIS : ce sont les quatre choses de la fenêtre « Ma chaîne » qui
 * restent à plat, avec leur nom.
 *
 *  - `surcharge` : la ligne « Surcharge +10 · plafond 110 » (la jauge d'énergie reste à plat, c'est sa ligne
 *    d'explication qui se range) ;
 *  - `reputation`, `fidelite`, `hype`, `momentum` : les jauges secondaires du HUD progressif ;
 *  - `ma_ligne` : l'axe lumière / chaos, quand l'alignement existe ;
 *  - `niveau` : « Niveau N · vidéos · PC » ;
 *  - `ratio` : « un abonné : N vues » ; `niche` : le bandeau de niche ;
 *  - `rivaux` : « Pendant ce temps, eux ».
 */
export const LIGNES_DU_DETAIL = [
  "surcharge",
  "reputation",
  "fidelite",
  "hype",
  "momentum",
  "ma_ligne",
  "niveau",
  "ratio",
  "niche",
  "rivaux",
] as const;

export type LigneDetail = (typeof LIGNES_DU_DETAIL)[number];

/**
 * **Le tableau de bord limpide est-il actif ?** `content/meta.json → tableau_limpide`. Absent ou `false` : le
 * tableau d'avant (toutes les jauges à plat, sans nom écrit), au pixel près ⟦A-10⟧.
 */
export function tableauLimpide(): boolean {
  return contenu().meta.tableau_limpide === true;
}

/**
 * Les lignes qui vont réellement dans « Détails » à cet instant, dans l'ordre de lecture. `presentes` dit ce
 * que l'écran SAURAIT afficher (le HUD progressif, l'escalier, la partie) ; rien à ranger ⇒ liste vide ⇒ pas de
 * bouton « Détails » du tout (une porte qui ne mène nulle part n'est pas une porte : à la seconde zéro, rien
 * n'est rangé, et le joueur n'a donc rien de plus à lire que ses trois jauges).
 */
export function lignesDuDetail(presentes: Readonly<Partial<Record<LigneDetail, boolean>>>): LigneDetail[] {
  return LIGNES_DU_DETAIL.filter((id) => presentes[id] === true);
}

/**
 * Les ancres (`data-decouverte-ancre`) et les halos de Matt qui visent un élément RANGÉ dans « Détails ». Quand
 * Matt présente la fidélité, le momentum ou la hype, l'élément dont il parle doit être sous les yeux : le
 * tableau ouvre alors « Détails » le temps de la leçon (sans toucher à la préférence du joueur), et le referme
 * ensuite. Une leçon qui montre du doigt une porte fermée serait pire que le tableau d'avant.
 */
const CIBLES_DU_DETAIL: ReadonlySet<string> = new Set(["reputation", "fidelite", "fil", "momentum"]);

/** `cible` (ancre d'une leçon ou halo d'une réplique) pointe-t-elle une ligne de « Détails » ? */
export function cibleDansLeDetail(cible: string | null | undefined): boolean {
  return typeof cible === "string" && CIBLES_DU_DETAIL.has(cible);
}
